<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { onMount } from 'svelte';
    import { ButtonLink } from '@oat-sa-private/ui-elements';
    import { __ } from '@oat-sa-private/ui-core';

    /**
     * @typedef {Object} Props
     * @property {String} src - the URL of the HTML document
     * @property {String} title - the document title
     * @property {String} width - the container width (css expression, not number)
     * @property {String} height - the container height (css expression, not number)
     * @property {String} maxHeight - the container max-height (css expression, not number)
     * @property {Object} [buttons] - which buttons should be displayed
     * @property {Function} onReady - called when the document frame is loaded
     * @property {Function} onError - called if the document doesn't load
     */
    /** @type {Props} */
    let {
        src,
        title,
        width,
        height,
        maxHeight,
        buttons = {
            errorOpenInNewTab: true
        },
        onReady,
        onError
    } = $props();

    const sandbox = '';

    let error = $state();

    function getContainerStyle() {
        return [
            width && `--html-viewer-width:${width}`,
            height && height !== 'auto' && `--html-viewer-height:${height}`,
            maxHeight && `--html-viewer-max-height:${maxHeight}`
        ]
            .filter(Boolean)
            .join(';');
    }

    /**
     * Set in error mode and dispatch the error event
     * @param {Error} err
     */
    function handleError(err) {
        error = err instanceof TypeError ? err.message : __('An error occurred while loading the document.');
        onError?.(err);
    }

    function handleLoad() {
        onReady?.();
    }

    onMount(() => {
        if (!src) {
            handleError(new TypeError(__('Missing document.')));
        }
    });
</script>

<style>
    .html-viewer {
        --html-viewer-width: 100%;
        --html-viewer-height: 100%;
        --html-viewer-max-height: none;
        background: var(--color-bg-info);
        width: var(--html-viewer-width);
        height: var(--html-viewer-height);
        max-height: var(--html-viewer-max-height);
    }

    .html-wrapper {
        position: relative;
        height: 100%;

        &.error > p {
            color: var(--color-text-default);
            margin: 2rem auto;
            text-align: center;
        }
    }

    .html-frame {
        display: block;
        width: 100%;
        height: 100%;
        border: 0;
        background: var(--color-bg-default);

        &:focus-visible {
            outline: var(--border-medium) dotted var(--color-border-focus);
            outline-offset: calc(-1 * var(--border-medium));
        }
    }
</style>

<div class="html-viewer inverted" style={getContainerStyle()}>
    <div class="html-wrapper" role="document" aria-label={title} class:error>
        {#if error}
            <p>{error}</p>
            {#if buttons.errorOpenInNewTab && src}
                <p>
                    <ButtonLink
                        href={src}
                        target="_blank"
                        title={__('Open in new tab: %s', title)}
                        iconAfter="open-new-window-16" />
                </p>
            {/if}
        {:else}
            <iframe
                class="html-frame"
                {sandbox}
                {src}
                {title}
                referrerpolicy="no-referrer"
                onload={handleLoad}
                onerror={handleError}></iframe>
        {/if}
    </div>
</div>
